import type { Field, FieldOption } from "@sdr/api/chat/identify/types";
import { TextPrompt } from "./text";
import { useCallback, useState } from "react";
import { Button } from "../../ui/button";
import { useChat } from "../../context/use-chat";

type FieldPromptProps = {
  field: Field;
};

export const MultipleChoice = ({ field }: FieldPromptProps) => {
  const [selected, setSelected] = useState<FieldOption[]>([]);
  const { append, isLoading } = useChat();

  const add = useCallback((option: FieldOption) => {
    setSelected((selected) => [...selected, option]);
  }, []);

  const remove = useCallback((option: FieldOption) => {
    setSelected((selected) => selected.filter((o) => o !== option));
  }, []);

  const handleSubmit = useCallback(() => {
    append(
      selected.map((o) => o.value).join(", "),
      selected.map((o) => o.id),
    );
  }, [append, selected]);

  return (
    <div className="bg-background px-4 py-5">
      <div className="flex flex-wrap gap-2">
        {field.options!.map((option) => {
          const active = selected.indexOf(option) >= 0;
          return (
            <Button
              key={`option_${option.id}`}
              variant={active ? "default" : "outline"}
              disabled={isLoading}
              onClick={() => {
                if (active) remove(option);
                else add(option);
              }}
            >
              {option.value}
            </Button>
          );
        })}
      </div>
      <Button
        className="mt-2"
        onClick={handleSubmit}
        disabled={isLoading || !selected.length}
      >
        Enviar
      </Button>
    </div>
  );
};

const SingleChoice = ({ field }: FieldPromptProps) => {
  const { append, isLoading } = useChat();

  const handleSubmit = useCallback(
    (option: FieldOption) => {
      append(option.value, [option.id]);
    },
    [append],
  );

  return (
    <div className="px-4 py-5">
      <div className="flex flex-wrap gap-2">
        {field.options!.map((option) => (
          <Button
            key={`option_${option.id}`}
            variant="outline"
            disabled={isLoading}
            onClick={() => {
              handleSubmit(option);
            }}
          >
            {option.value}
          </Button>
        ))}
      </div>
    </div>
  );
};

export const FieldPrompt = ({ field }: FieldPromptProps) => {
  if (field.type === "choice") {
    return <SingleChoice field={field} />;
  }

  if (field.type === "multi_choice") {
    return <MultipleChoice field={field} />;
  }

  return <TextPrompt />;
};
